// ============================================================================
// EL TELÉFONO DEL PACIENTE: cuándo es un fijo, cómo se rotula y cuándo se pinta.
// ============================================================================
//
// QUÉ PROBLEMA RESUELVE. La ficha del paciente pintaba TODOS los campos de contacto, tuvieran dato
// o no: una fila «Teléfono 1  —», otra «Fax  —», otra «Web  —». En una ficha normal eso son cinco
// huecos vacíos delante del único teléfono que de verdad existe. Y el rótulo tampoco ayudaba:
// «Teléfono 1» no le dice nada a nadie; lo que hay ahí, casi siempre, es el fijo de casa.
//
// LAS TRES REGLAS, Y POR QUÉ ESTÁN AQUÍ Y NO ESPARCIDAS POR LAS PANTALLAS:
//
//   1. UN CAMPO DE CONTACTO VACÍO NO SE PINTA (`tieneDato`). Ni etiqueta ni hueco. Esto es la VISTA;
//      los FORMULARIOS siguen pintando el campo vacío, faltaría más: si no, no se podría rellenar
//      nunca. Por eso la decisión no vive dentro de un componente de campo, sino aquí, y cada
//      pantalla decide si está enseñando o dejando escribir.
//
//   2. LA ETIQUETA DEPENDE DEL NÚMERO (`etiquetaTelefono`). Si es un fijo, «Teléfono fijo»; si no lo
//      es, «Teléfono» a secas.
//
//   3. NUNCA SE OCULTA UN NÚMERO QUE EXISTE. Esto no es negociable y es la razón de que las dos
//      reglas anteriores estén separadas: se oculta lo VACÍO, jamás lo que "no parece un fijo". En
//      fichas importadas de otro software es de lo más normal encontrar un móvil guardado en el
//      campo del fijo; ese número se ve igual, solo que con la etiqueta neutra. Perder un teléfono
//      de vista es mucho peor que la molestia que estamos arreglando.
//
// ESTO ES UNA CONVENCIÓN DE UN PAÍS, NO UNA VERDAD. En España los fijos empiezan por 9 (y por 8) y
// los móviles por 6 y 7. Es el plan de numeración español y solo vale aquí: el día que la app se
// venda fuera, o que Portugal cambie el suyo, SE TOCA `CONVENCION_TELEFONO` y nada más. Por eso la
// regla está nombrada, en una sola constante y con este comentario encima, en vez de repartida en
// media docena de `.startsWith('9')` por las pantallas.
//
// Y ANTE LA DUDA, ETIQUETA NEUTRA: un número que no encaja en el plan nacional (extranjero, mal
// escrito, incompleto) NO es un fijo a efectos del rótulo, pero se sigue viendo entero.

// La convención del plan de numeración. UN SOLO SITIO.
import { t } from './i18n.js'   // B-2259 · los textos, en el idioma de quien mira (utils/i18n)

export const CONVENCION_TELEFONO = {
  pais: 'ES',
  // Prefijo internacional, tal y como puede venir escrito en la ficha (+34, 0034, 34).
  prefijoInternacional: '34',
  // Cuántos dígitos tiene un número nacional completo.
  longitudNacional: 9,
  // Primer dígito de un teléfono FIJO.
  fijo: ['8', '9'],
  // Primer dígito de un MÓVIL. No se usa para decidir nada (lo que no es fijo lleva etiqueta
  // neutra), pero se documenta aquí para que el día que haya que cambiar el plan se vea entero.
  movil: ['6', '7'],
}

// ¿Este campo tiene algo que enseñar? Vacío, en blanco o sin definir → no se pinta.
// Un `false` o un 0 SÍ son un dato (aunque en contacto no los haya): solo se descarta el hueco.
export function tieneDato(v) {
  if (v === null || v === undefined) return false
  if (typeof v === 'string') return v.trim() !== ''
  return true
}

// Los campos de una lista que este objeto tiene Y con dato. Es lo que usa la vista para no pintar
// filas vacías; la lista de campos la pone quien llama (la vista sabe qué grupo está pintando).
export function camposConDato(campos, obj) {
  if (!Array.isArray(campos) || !obj) return []
  return campos.filter(k => k in obj && tieneDato(obj[k]))
}

// El número en dígitos, sin prefijo internacional, espacios, guiones ni paréntesis.
// El prefijo SOLO se quita si lo que queda tiene la longitud nacional: así un número de otro país
// (o uno mal escrito) no se muerde por la mitad.
export function normalizarTelefono(v) {
  let d = String(v == null ? '' : v).replace(/\D+/g, '')
  if (!d) return ''
  const { prefijoInternacional: pre, longitudNacional: n } = CONVENCION_TELEFONO
  if (d.length === 2 + pre.length + n && d.startsWith('00' + pre)) d = d.slice(2 + pre.length)
  else if (d.length === pre.length + n && d.startsWith(pre)) d = d.slice(pre.length)
  return d
}

// ¿Es un teléfono FIJO según la convención del país? Ante cualquier duda, false → etiqueta neutra
// (que NO es ocultarlo: el número se sigue viendo).
export function esTelefonoFijo(v) {
  const d = normalizarTelefono(v)
  if (d.length !== CONVENCION_TELEFONO.longitudNacional) return false
  return CONVENCION_TELEFONO.fijo.includes(d[0])
}

// La etiqueta que se le pone a ese número. `neutra` permite que una pantalla concreta diga
// «Teléfono 2» en vez de «Teléfono» cuando el fijo no encaja.
export const ETIQUETA_TELEFONO_FIJO = 'Teléfono fijo'
export const ETIQUETA_TELEFONO_NEUTRA = 'Teléfono'
export function etiquetaTelefono(v, neutra = ETIQUETA_TELEFONO_NEUTRA) {
  return esTelefonoFijo(v) ? ETIQUETA_TELEFONO_FIJO : neutra
}


// ============================================================================
// EL MÓVIL AL QUE SE ESCRIBE, Y EL TELÉFONO AL QUE SE LLAMA — EL ESPEJO DE BACKEND
// ============================================================================
//
// ESTO ES EL ESPEJO EXACTO de `backend/services/movilPaciente.js`. Las dos mitades tienen que
// contestar LO MISMO y hay una guardia que las ejecuta a la vez, caso por caso, y se pone roja si
// se separan: `backend/routes/movil-las-dos-mitades-de-acuerdo.test.js`. Si tocas una, toca la otra.
//
// POR QUÉ HACE FALTA AQUÍ y no basta con preguntarle al servidor: hay pantallas que ya tienen la
// ficha entera en la mano (los tres teléfonos vienen en el JSON) y deciden EN EL NAVEGADOR si se
// enseña el botón de WhatsApp o el de llamar. Ahí escribían `pv.tel_movil || pv.tel1` — el criterio
// de ayer, el que deja fuera a quien tiene el móvil en «Teléfono 2» y abre WhatsApp contra un fijo.
//
// SON DOS PREGUNTAS DISTINTAS Y TIENEN DOS RESPUESTAS DISTINTAS:
//   · `movilParaEscribir` — «¿a qué número le mando el WhatsApp?». Un fijo español NO vale: no lo
//     lee nadie y Meta cobra el mensaje igual.
//   · `telefonoParaLlamar` — «¿qué teléfono le enseño a quien va a llamarle?». Aquí un fijo es el
//     número CORRECTO. No se filtra nada: el primero de los tres que tenga algo.
// Una pantalla que enseña un teléfono Y ofrece escribir pide LAS DOS.

// Los campos de la ficha donde puede estar el móvil, EN ORDEN DE PREFERENCIA.
export const CAMPOS_TELEFONO = ['tel_movil', 'tel1', 'tel2']

// Los patrones del plan de numeración español, con la misma forma que los de backend.
const RE_MOVIL_ES = /^(0034|34)?[67][0-9]{8}$/
const RE_FIJO_ES = /^(0034|34)?[89][0-9]{8}$/
const RE_E164 = /^[1-9][0-9]{7,14}$/
// «Aquí hay DOS números» (o un número y una aclaración). Mismos separadores que
// backend/services/telefonoE164: se manda al PRIMERO, así que es el primero el que hay que mirar.
const SEPARADORES = /[/;,]| y | o |\n/i

const digitos = (v) => String(v == null ? '' : v).replace(/[^0-9]/g, '')
const vacio = (v) => String(v == null ? '' : v).trim() === ''
const limpio = (v) => String(v == null ? '' : v).trim()

// LA CLAVE DE UN TELÉFONO: sus nueve últimos dígitos, o null si tiene menos de nueve. Es el espejo de
// `claveTel` de backend/services/movilPaciente.js (la misma que compara el servidor en SQL con
// RIGHT(...,9)), y la guardia backend/routes/telefono-nueve-digitos-botones-whatsapp.test.js ejecuta
// las dos mitades con los mismos casos. Por eso el 9 va escrito aquí y no sale de CONVENCION_TELEFONO:
// no es el plan de numeración de un país, es la clave con la que la casa entera cruza números.
//
// UN NÚMERO DE MENOS DE NUEVE DÍGITOS NO ES UN TELÉFONO. Sirve para contestar «¿esta conversación es la
// de este número?» comparando claves ENTERAS, nunca «acaba en». El botón de WhatsApp de la ficha y el
// de un seguimiento cortaban el número en nueve y elegían la conversación que ACABABA así, sin exigir
// que hubiera nueve: con un móvil mal apuntado de ocho cifras («60011223») abrían la conversación de
// OTRA persona («34660011223»). Quien la use trata el null como «no busques».
export function claveTel(v) {
  const d = digitos(v)
  return d.length >= 9 ? d.slice(-9) : null
}

// ¿Es un MÓVIL español? Ante la duda, no.
export function esMovilEs(v) { return RE_MOVIL_ES.test(digitos(v)) }

// ¿Lo que DE VERDAD se mandaría es un fijo español? No es lo mismo que «este campo es un fijo»:
// «965215755 / 600112233» tiene dieciocho dígitos y no casa con el patrón, pero se manda al primero,
// que es el fijo de casa. Sin esta comprobación el rescate volvería a colar el mensaje que no lee
// nadie y que se paga igual.
export function esFijoAlEnviar(v) {
  if (vacio(v)) return false
  if (RE_FIJO_ES.test(digitos(v))) return true
  const primero = digitos(String(v).split(SEPARADORES)[0])
  if (RE_FIJO_ES.test(primero)) return true
  // Y EL FIJO QUE SE ESCONDE DETRAS DEL CERO DE SALIDA (revision 18-sep). `telefonoE164.aE164` lleva
  // un `while (d.startsWith('0')) d = d.slice(1)`, asi que «086 123 4567» sale `34861234567`: un fijo
  // español que no es de nadie. Esta mitad no lo miraba y decia que SI se le podia escribir, mientras
  // el backend decia que no — y el boton de WhatsApp de la ficha se encendia sobre un numero que el
  // servidor ya estaba vetando. Las dos mitades tienen que decir lo mismo del mismo campo.
  return RE_FIJO_ES.test(primero.replace(/^0+/, ''))
}

// Un número extranjero escrito COMO TAL (con «+» o «00»). Sin esa marca no se adivina de qué país
// es: se mandaría a alguien, y ese alguien no es el paciente.
export function esInternacional(v) {
  const s = String(v == null ? '' : v).trim()
  if (!/^(\+|00)/.test(s)) return false
  let d = digitos(s)
  if (d.startsWith('00')) d = d.slice(2)
  if (d.startsWith('34')) return false
  return RE_E164.test(d)
}

/**
 * EL MÓVIL DE LA FICHA, y de qué campo sale. Devuelve { numero, campo, via } o null.
 * La escalera, en orden y con el mismo porqué que en backend:
 *  1. `tel_movil`, salvo que lo escrito ahí sea un FIJO español (en Gesden ese campo se rellena
 *     solo, así que el nombre de la casilla no es una declaración de nadie: manda el número).
 *  2. un MÓVIL español en «Teléfono» o «Teléfono 2».
 *  3. un INTERNACIONAL en «Teléfono» o «Teléfono 2».
 *  4. rescate «como ayer», solo desde «Teléfono» y nunca a un fijo: lo que ya se le escribía.
 */
export function movilDeFicha(ficha) {
  if (!ficha) return null
  const [MOVIL, T1, T2] = CAMPOS_TELEFONO
  // t396 · 0 · el que la clínica ha elegido en la ficha para los WhatsApp y avisos (`tel_whatsapp`), si no es un fijo. Espejo
  // de backend/services/movilPaciente.campoWhatsappElegido.
  const el = campoWhatsappElegido(ficha)
  if (el) return { numero: limpio(ficha[el]), campo: el, via: 'elegido' }
  const tm = ficha[MOVIL]
  if (!vacio(tm) && !esFijoAlEnviar(tm)) return { numero: limpio(tm), campo: MOVIL, via: 'campo_movil' }
  for (const campo of [T1, T2]) if (!vacio(ficha[campo]) && esMovilEs(ficha[campo])) return { numero: limpio(ficha[campo]), campo, via: 'movil_es' }
  for (const campo of [T1, T2]) if (!vacio(ficha[campo]) && esInternacional(ficha[campo])) return { numero: limpio(ficha[campo]), campo, via: 'internacional' }
  if (!vacio(ficha[T1]) && !esFijoAlEnviar(ficha[T1])) return { numero: limpio(ficha[T1]), campo: T1, via: 'rescate' }
  return null
}

/** t396 · El campo elegido para los WhatsApp y avisos ('tel_movil' | 'tel1' | 'tel2') si tiene número y no es un fijo; si no, null. */
export function campoWhatsappElegido(ficha) {
  if (!ficha) return null
  const c = String(ficha.tel_whatsapp == null ? '' : ficha.tel_whatsapp).trim()
  if (!CAMPOS_TELEFONO.includes(c) || vacio(ficha[c]) || esFijoAlEnviar(ficha[c])) return null
  return c
}

/**
 * t396 · AL GUARDAR LA FICHA, el número para WhatsApp y avisos viaja SOLO si se ha cambiado respecto a lo que había al pulsar
 * «Editar» (`antes`): así guardar otra cosa de la ficha nunca lo toca. Si el teléfono elegido se ha vaciado, viaja '' (vuelve a
 * «el de siempre»). Cambia `payload` y lo devuelve.
 */
export function numeroWhatsappAlGuardar(payload, antes) {
  if (!payload || !('tel_whatsapp' in payload)) return payload
  const wa = String(payload.tel_whatsapp || '')
  if (wa && vacio(payload[wa])) payload.tel_whatsapp = ''
  else if (antes && wa === String(antes.tel_whatsapp || '')) delete payload.tel_whatsapp
  return payload
}

/** Solo el número al que se le puede ESCRIBIR, o null. Reemplazo directo de `tel_movil || tel1`. */
export function movilParaEscribir(ficha) {
  const m = movilDeFicha(ficha)
  return m ? m.numero : null
}

// B-581 · EL QUE SE ELIGE EN SU FICHA («Se le llama al», en Datos): `tel_contacto` dice QUÉ CAMPO; si está vacío, o ese
// campo ya no tiene número, el primero de los tres que tenga algo. Espejo de backend/services/movilPaciente.campoContacto.
/** De qué campo sale el teléfono para llamar ('tel_movil' | 'tel1' | 'tel2'), o null. */
export function campoParaLlamar(ficha) {
  if (!ficha) return null
  const elegido = ficha.tel_contacto
  if (CAMPOS_TELEFONO.includes(elegido) && !vacio(ficha[elegido])) return elegido
  for (const campo of CAMPOS_TELEFONO) if (!vacio(ficha[campo])) return campo
  return null
}

/** El teléfono que se le ENSEÑA a quien va a LLAMAR: el elegido en su ficha, o el primero de los tres que tenga algo. */
export function telefonoParaLlamar(ficha) {
  const campo = campoParaLlamar(ficha)
  return campo ? limpio(ficha[campo]) : null
}

/**
 * POR QUÉ NO SE LE PUEDE ESCRIBIR — con las mismas palabras que el servidor. «No tiene teléfono» y
 * «tiene un fijo» son problemas distintos y quien lo lee hace cosas distintas con cada uno: al
 * primero hay que pedirle el número, al segundo hay que preguntarle si tiene móvil.
 */
export function motivoSinMovil(ficha) {
  if (movilParaEscribir(ficha)) return null
  const otros = CAMPOS_TELEFONO.map(c => (ficha ? ficha[c] : null)).filter(v => !vacio(v))
  if (!otros.length) return t('utils.telefono.el_paciente_no_tiene_ningun', 'El paciente no tiene ningún teléfono en su ficha')
  if (otros.some(v => esFijoAlEnviar(v))) return t('utils.telefono.el_paciente_no_tiene_movil', 'El paciente no tiene móvil: el teléfono de su ficha es un fijo')
  return t('utils.telefono.el_paciente_no_tiene_movil_2', 'El paciente no tiene móvil: el teléfono de su ficha no es un número al que se pueda escribir')
}
